import ConstantData from "../Utils/Constant";
import { commonFormatNumber } from "./dataHelper";

/* -------------------- CONFIG -------------------- */
const EDGES = [
  0,
  50,
  150,
  400,
  1000,
  3000,
  8000,
  15000,
  22000,
  40000,
  60000,
  100000,
  150000,
  220000,
  300000,
  500000,
  Infinity,
];

const MICRO_MODE_THRESHOLD = 10; // small-sample heuristic
const MIN_FEATURES_FOR_HYST = 15; // don't attempt hysteresis for tiny datasets
const HYSTERESIS_FACTOR = 0.15; // +/-15% tolerance when testing membership (was 0.2)
const HYSTERESIS_MAX_FRAC = 0.15; // if weighted fraction > this → recompute (was 0.25)
const MIN_BINS = 5;
const MAX_BINS = 6;
const EDGE_MERGE_PENALTY = 3.0; // multiply delta for merges that would cause edge swallowing
const CENTER_THRESHOLD = 0.1;

/* -------------------- PER-LAYER STATE -------------------- */
const layerState = {}; // keyed by layerId: { previousBinRanges, previousZoom, previousCenter, lastMin, lastMax }

/* -------------------- HELPERS -------------------- */
function edgeFloor(x) {
  for (let i = EDGES.length - 1; i >= 0; i--)
    if (x >= EDGES[i]) return EDGES[i];
  return 0;
}
function edgeCeil(x) {
  for (let i = 0; i < EDGES.length; i++) if (x < EDGES[i]) return EDGES[i];
  return Infinity;
}
function binIndexFor(x) {
  // find i such that EDGES[i] <= x < EDGES[i+1]
  let lo = 0,
    hi = EDGES.length - 2;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (x < EDGES[mid]) hi = mid - 1;
    else if (x >= EDGES[mid + 1]) lo = mid + 1;
    else return mid;
  }
  return EDGES.length - 2;
}

function collectAADTValues(map, layerId) {
  const values = [];
  map
    .queryRenderedFeatures({ layers: [layerId], filter: ["has", "aadt_count"] })
    .forEach((f) => {
      const a = f.properties?.aadt_count;
      if (typeof a === "number" && a > 0) values.push(a);
    });
  return values;
}

/* Build histogram over EDGES, keep log stats for SSE merging */
function buildFixedHistogram(aadtValues) {
  const bins = Array.from({ length: EDGES.length - 1 }, (_, i) => ({
    i,
    min: EDGES[i],
    max: EDGES[i + 1],
    count: 0,
    sumLog: 0,
    sumLog2: 0,
  }));
  for (const v of aadtValues) {
    const i = binIndexFor(v);
    const lv = Math.log(v);
    const b = bins[i];
    b.count++;
    b.sumLog += lv;
    b.sumLog2 += lv * lv;
  }
  return bins.filter((b) => b.count > 0);
}

function sse(count, sum, sum2) {
  return sum2 - (sum * sum) / Math.max(count, 1);
}

/* Merge adjacent bins until <= maxBins.
   Penalize merges which would swallow edge bins (so edges are preserved). */
function mergeToTargetBins(fixedBins, minBins = MIN_BINS, maxBins = MAX_BINS) {
  // compute log centers (geometric center)
  for (const b of fixedBins) {
    const a = Math.max(b.min || 1, 1); // avoid log(0)
    const c = b.max === Infinity ? a * 2 : Math.sqrt(a * b.max);
    b.logCenter = Math.log(c);
  }

  const bins = fixedBins.map((b) => ({ ...b }));

  while (bins.length > maxBins) {
    let best = { idx: -1, delta: Infinity };

    for (let i = 0; i < bins.length - 1; i++) {
      const L = bins[i],
        R = bins[i + 1];
      const c = L.count + R.count;
      const s = L.sumLog + R.sumLog;
      const s2 = L.sumLog2 + R.sumLog2;
      // Δ=SSE(L∪R)−SSE(L)−SSE(R) formula for calculation Delta

      let delta =
        sse(c, s, s2) -
        sse(L.count, L.sumLog, L.sumLog2) -
        sse(R.count, R.sumLog, R.sumLog2);

      // Penalize merges that include the extreme left or extreme right bins
      // so edges are less likely to be swallowed into middle bins.
      if (i === 0 || i + 1 === bins.length - 1) delta *= EDGE_MERGE_PENALTY;

      if (delta < best.delta) best = { idx: i, delta };
    }

    // Merge the best pair (adjacent)
    const i = best.idx;
    const L = bins[i],
      R = bins[i + 1];
    bins.splice(i, 2, {
      i: L.i,
      min: L.min,
      max: R.max,
      count: L.count + R.count,
      sumLog: L.sumLog + R.sumLog,
      sumLog2: L.sumLog2 + R.sumLog2,
      logCenter:
        0.5 *
        (Math.log(Math.max(L.min, 1)) +
          Math.log(R.max === Infinity ? L.min * 2 : R.max)), //I am not sure this is needed
    });
  }

  // If we ended with < minBins but >0 (rare for sparse views), we just return what we have.
  return bins.map((b) => ({ min: b.min, max: b.max, count: b.count }));
}

/* Micro mode for tiny datasets: 1-3 bins snapped to EDGES */
function shouldUseMicroMode(values) {
  if (values.length < MICRO_MODE_THRESHOLD) return true;
  const lv = values.map(Math.log);
  const spread = Math.max(...lv) - Math.min(...lv);
  return spread < 1.5; // ~4.5x
}

function computeMicroBins(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const minV = sorted[0],
    maxV = sorted[sorted.length - 1];

  // If all very similar 1 snapped bin
  if (maxV / minV < 2) {
    return [
      { min: edgeFloor(minV), max: edgeCeil(maxV), count: values.length },
    ];
  }

  // Try 2–3 bins by gaps in ln(aadt), but snap to fixed edges
  const lv = sorted.map(Math.log);
  const gaps = [];
  for (let i = 1; i < lv.length; i++) gaps.push({ i, g: lv[i] - lv[i - 1] });
  gaps.sort((a, b) => b.g - a.g);

  const bins = [];
  const addRange = (lo, hi) => {
    const min = edgeFloor(sorted[lo]);
    const max = edgeCeil(sorted[hi]);
    const count = hi - lo + 1;
    if (count > 0) bins.push({ min, max, count });
  };

  // Big enough largest gap 2 bins
  if (gaps.length && gaps[0].g > 0.5) {
    const s1 = gaps[0].i;
    addRange(0, s1 - 1);
    addRange(s1, sorted.length - 1);

    // Maybe a 3rd bin
    if (gaps.length > 1 && gaps[1].g > 0.4) {
      const s2 = gaps[1].i;
      // Re-snap by inserting a middle bin
      bins.length = 0;
      const [a, b] = [Math.min(s1, s2), Math.max(s1, s2)];
      addRange(0, a - 1);
      addRange(a, b - 1);
      addRange(b, sorted.length - 1);
    }

    return coalesceAdjacent(bins); // merges if snapping made two ranges equal
  }

  // Default: 2 evenly split bins (snapped)
  const mid = Math.floor(sorted.length / 2);
  addRange(0, mid - 1);
  addRange(mid, sorted.length - 1);
  return coalesceAdjacent(bins);
}

function coalesceAdjacent(ranges) {
  if (!ranges.length) return ranges;
  ranges.sort((x, y) => x.min - y.min);
  const out = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const last = out[out.length - 1],
      cur = ranges[i];
    if (last.max === cur.min) {
      last.max = cur.max;
      last.count += cur.count;
    } else {
      out.push(cur);
    }
  }
  return out.slice(0, 3);
}

/* -------------------- HYSTERESIS --------------------
   Asymmetrical importance: small values weigh more so low-value moves matter more.
   Only apply hysteresis if:
     - we have previous ranges,
     - we have enough features,
     - zoom is not a zoom-out,
     - center didn't move much.
*/
function importanceWeight(v) {
  // small values => larger weight. Use log-scale inverse.
  // +10 avoids division by 0; tune as needed.
  return 1 / Math.max(0.2, Math.log10(v + 10));
}

function applyHysteresis(
  currentValues,
  prevRanges,
  variation,
  deadband = HYSTERESIS_FACTOR
) {
  if (!prevRanges) return null;
  if (!currentValues || currentValues.length < MIN_FEATURES_FOR_HYST)
    return null; // don't apply hysteresis for very small sample

  // weighted moved count
  let weightedMove = 0;
  let totalWeight = 0;

  for (const v of currentValues) {
    const w = importanceWeight(v);
    totalWeight += w;
    const inPrev = prevRanges.find(
      (r) =>
        v >= r.min * (1 - deadband) &&
        v < (r.max === Infinity ? Infinity : r.max * (1 + deadband))
    );
    if (!inPrev) weightedMove += w;
  }

  const fracMove = weightedMove / Math.max(totalWeight, 1);

  if (variation < 0 && weightedMove > 0) return null; // disable hysteresis on zoom-out

  // stricter: if <= HYSTERESIS_MAX_FRAC keep previous ranges
  if (fracMove <= HYSTERESIS_MAX_FRAC) {
    // refresh counts using strict bins (no deadband)
    return prevRanges
      .map((r) => ({
        ...r,
        count: currentValues.filter(
          (v) => v >= r.min && v < (r.max === Infinity ? Infinity : r.max)
        ).length,
      }))
      .filter((r) => r.count > 0);
  }
  return null;
}

function buildColorExpression(
  ranges,
  property = "aadt_count",
  colorCodes,
  ColorCodeIndexes
) {
  const expr = ["case"];

  // Normal bin checks
  ranges.forEach((r, i) => {
    expr.push(
      [
        "all",
        [">=", ["get", property], r.min],
        ["<", ["get", property], r.max],
      ],
      colorCodes[ColorCodeIndexes[i]]
    );
  });

  // Fallback: snap stray values to nearest bin color
  expr.push([
    "let",
    "val",
    ["get", property],
    [
      "case",
      ...ranges.flatMap((r, i) => [
        ["==", ["get", property], r.min],
        colorCodes[ColorCodeIndexes[i]],
        ["==", ["get", property], r.max],
        colorCodes[ColorCodeIndexes[i]],
      ]),
      colorCodes[ColorCodeIndexes[ranges.length - 1]], // default last bin color
    ],
  ]);

  return expr;
}

const formatLegend = (bin, pos, total, layerMin, layerMax) => {
  // Provide double-bounded edges for first/last bins using observed min/max
  const min = bin.min;
  const max = bin.max;

  if (pos === 0 && layerMin != null) {
    // show lower bin as [roundedFloor(layerMin) – max]
    const floorMin = edgeFloor(layerMin);
    const lower =
      floorMin === min ? commonFormatNumber(min) : commonFormatNumber(floorMin);
    return `${lower}–${commonFormatNumber(max)}`;
  }
  if (pos === total - 1 && layerMax != null) {
    // show upper bin as [min – roundedCeil(layerMax)]
    const ceilMax = edgeCeil(layerMax);
    const upper =
      ceilMax === max ? commonFormatNumber(max) : commonFormatNumber(ceilMax);
    return `${commonFormatNumber(min)}–${upper}`;
  }
  return `${commonFormatNumber(min)}–${commonFormatNumber(max)}`;
};

function fixArray(arr) {
  arr.sort((a, b) => a - b);
  let result = [];
  for (let i = 0; i < arr.length; i++) {
    let num = arr[i];
    while (result.includes(num) && num > 0) num--;
    while (result.includes(num) && num < 5) num++;
    if (!result.includes(num)) result.push(num);
  }
  if (arr.length >= 6) result = [0, 1, 2, 3, 4, 5];
  return result.sort((a, b) => a - b);
}

const makeEmpty = () => ({
  binData: [],
  legends: Array(ConstantData.advancedMapOverlaysIds.trafficSplitIndicesWithZero.length).fill(""),
  legendIndexes: [],
  colorExpr: [],
});

/* -------------------- MAIN EXPORT -------------------- */
export const buildTrafficLayer = ({ map, layerId, zoom, colorCodes }) => {
  if (!map || !map.getLayer(layerId) || zoom < 10) return makeEmpty();

  // per-layer state init
  if (!layerState[layerId])
    layerState[layerId] = {
      previousBinRanges: null,
      previousZoom: null,
      previousCenter: null,
      lastMin: null,
      lastMax: null,
    };
  const state = layerState[layerId];

  const currentCenter = map.getCenter();
  const aadtValues = collectAADTValues(map, layerId);
  if (!aadtValues.length) return makeEmpty();

  // record observed min/max for labeling & edge bounding
  const observedMin = Math.min(...aadtValues);
  const observedMax = Math.max(...aadtValues);
  state.lastMin = observedMin;
  state.lastMax = observedMax;

  let ranges;
  if (shouldUseMicroMode(aadtValues)) {
    ranges = computeMicroBins(aadtValues);
  } else {
    // decide whether we should attempt hysteresis
    const variation =
      typeof state.previousZoom === "number" ? zoom - state.previousZoom : 0;
    // disable hysteresis on zoom-out or if moved too far
    const tryHysteresis =
      state.previousBinRanges && Math.abs(zoom - state.previousZoom) < 2;
    const sticky = tryHysteresis
      ? applyHysteresis(aadtValues, state.previousBinRanges, variation)
      : null;

    ranges =
      sticky ??
      mergeToTargetBins(buildFixedHistogram(aadtValues), MIN_BINS, MAX_BINS);
  }
  // prepare binData and legends (double-bounded edges for first/last)
  const binData = ranges.map((r, i) => ({
    ...r,
    color: colorCodes[Math.min(i, colorCodes.length - 1)],
    legend: formatLegend(r, i, ranges.length, state.lastMin, state.lastMax),
  }));

  const ColorCodeIndexes = binData.map((b) => {
    return ConstantData.advancedMapOverlaysIds.Ranges.find((ele) => b.max <= ele.value).i;
  });

  // Build color expression ONCE, always use 'aadt_count'
  const colorExpr = buildColorExpression(
    binData,
    "aadt_count",
    colorCodes,
    fixArray(ColorCodeIndexes)
  );
  map.setPaintProperty(layerId, "line-color", colorExpr);

  // update state
  state.previousBinRanges = binData;
  state.previousZoom = zoom;
  state.previousCenter = currentCenter;

  return {
    binData,
    legends: binData.map((b) => b.legend),
    legendIndexes: fixArray(ColorCodeIndexes),
    colorExpr,
  };
};
